import { produce } from 'immer'
import {
  SET_RECEIPT_ATTACHMENT_IDS,
  SetReceiptAttachmentIdsAction,
} from 'src/app/RequestVirtualCard/redux/requestVirtualCard.actions'
import { isHandledPaywall402 } from 'src/component-modal/SaasActionRequiredModal/SaasActionRequiredModal'
import { requestVirtualCardRouteProps } from 'src/navigation/routeProps'
import {
  UPLOAD_ATTACHMENT_FAILURE,
  UPLOAD_ATTACHMENT_INIT,
  UPLOAD_ATTACHMENT_SUCCESS,
  UploadAttachmentActions,
} from 'src/redux/actions/data/receiptAttachments.actions'
import {
  POST_VIRTUAL_CARD_FAILURE,
  POST_VIRTUAL_CARD_INIT,
  POST_VIRTUAL_CARD_SUCCESS,
  PostVirtualCardActions,
} from 'src/redux/actions/data/virtualCards.actions'
import { createFlyoutReducer } from 'src/redux/genericReducers'
import RecentUsersFullPaginatedDataReducer, {
  Actions as RecentUsersFullPaginatedDataActions,
  initialState as initialRecentUsersFullPaginatedDataState,
  RecentUsersFullPaginatedDataState,
} from 'src/redux/recentUsersFullPaginatedData.reducer'
import UsersFullPaginatedDataReducer, {
  Actions as UsersFullPaginatedDataActions,
  initialState as initialUsersFullPaginatedDataState,
  UsersFullPaginatedDataState,
} from 'src/redux/usersFullPaginatedData.reducer'
import { ErrorDetailMap, parseBackendErrors } from 'src/utils/error.utils'

export interface RequestVirtualCardBaseState {
  receiptAttachmentIds: string[]
  uploadingAttachmentThumbnails: Record<string, string>
  postingVirtualCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
}

export const initialBaseState: RequestVirtualCardBaseState = {
  receiptAttachmentIds: [],
  uploadingAttachmentThumbnails: {},
  postingVirtualCard: false,
}

export type BaseActions =
  | UploadAttachmentActions
  | PostVirtualCardActions
  | SetReceiptAttachmentIdsAction

const BaseRequestVirtualCardReducer = (
  state = initialBaseState,
  action: BaseActions,
): RequestVirtualCardBaseState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case SET_RECEIPT_ATTACHMENT_IDS: {
        draft.receiptAttachmentIds = action.payload.ids
        return draft
      }

      case UPLOAD_ATTACHMENT_INIT: {
        draft.uploadingAttachmentThumbnails = {
          ...draft.uploadingAttachmentThumbnails,
          [action.payload.uploadId]: action.payload.thumbnailUrl,
        }
        return draft
      }

      case UPLOAD_ATTACHMENT_FAILURE: {
        draft.uploadingAttachmentThumbnails = Object.keys(
          draft.uploadingAttachmentThumbnails,
        )
          .filter((id) => id !== action.payload.uploadId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentThumbnails[curr],
            }),
            {},
          )
        return draft
      }

      case UPLOAD_ATTACHMENT_SUCCESS: {
        draft.uploadingAttachmentThumbnails = Object.keys(
          draft.uploadingAttachmentThumbnails,
        )
          .filter((id) => id !== action.payload.uploadId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentThumbnails[curr],
            }),
            {},
          )
        draft.receiptAttachmentIds = [
          ...draft.receiptAttachmentIds,
          action.payload.id,
        ]
        return draft
      }

      case POST_VIRTUAL_CARD_INIT: {
        draft.postingVirtualCard = true
        draft.topLevelError = undefined
        draft.errorDetailMap = undefined
        return draft
      }

      case POST_VIRTUAL_CARD_SUCCESS: {
        draft.postingVirtualCard = false
        return draft
      }

      case POST_VIRTUAL_CARD_FAILURE: {
        draft.postingVirtualCard = false
        if (isHandledPaywall402(action.payload)) {
          return draft
        }
        const errors = parseBackendErrors(action.payload)
        draft.topLevelError = errors.topLevelError
        draft.errorDetailMap = errors.errorDetailMap
        return draft
      }
    }
  })
}

export type RequestVirtualCardState = RequestVirtualCardBaseState & {
  usersFullPaginatedData: UsersFullPaginatedDataState
  recentUsersFullPaginatedData: RecentUsersFullPaginatedDataState
}

export const initialState: RequestVirtualCardState = {
  ...initialBaseState,
  usersFullPaginatedData: initialUsersFullPaginatedDataState,
  recentUsersFullPaginatedData: initialRecentUsersFullPaginatedDataState,
}

export type Actions =
  | BaseActions
  | UsersFullPaginatedDataActions
  | RecentUsersFullPaginatedDataActions

export const CoreRequestVirtualCardReducer = (
  state = initialState,
  action: Actions,
): RequestVirtualCardState => ({
  ...BaseRequestVirtualCardReducer(state, action as BaseActions),
  usersFullPaginatedData: UsersFullPaginatedDataReducer(
    state.usersFullPaginatedData,
    action as UsersFullPaginatedDataActions,
  ),
  recentUsersFullPaginatedData: RecentUsersFullPaginatedDataReducer(
    state.recentUsersFullPaginatedData,
    action as RecentUsersFullPaginatedDataActions,
  ),
})

const RequestVirtualCardReducer = createFlyoutReducer(
  requestVirtualCardRouteProps,
  initialState,
  CoreRequestVirtualCardReducer,
)

export default RequestVirtualCardReducer
